import React, { Fragment, Suspense, useRef } from 'react';
import {
  Box,
  List,
} from '@mui/material';

import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useInView } from '@shared/hooks/useInView';

import { useInfiniteUniversities } from '../hooks/useInfiniteUniversities';

interface ViewProps {
  filters?: Universities.GetListParams;
  renderItem: (university: Universities.University) => React.ReactNode;
  renderSkeleton: () => React.ReactNode;
}

export const InfiniteUniversitiesList: React.FC<ViewProps> = ({
  filters = {},
  renderItem,
  renderSkeleton,
}) => {
  const { universities, status, fetchNextPage, hasNextPage, isFetching } = useInfiniteUniversities(filters);

  const loadNextRef = useRef<HTMLElement>(undefined);
  const isInView = useInView(loadNextRef);

  const _renderSkeleton = React.useCallback(() => {
    if (isFetching) {
      return Array.from({ length: 3 }).map((v, idx) => <Fragment key={idx}>{renderSkeleton()}</Fragment>);
    }
  }, [isFetching, renderSkeleton]);

  React.useEffect(() => {
    if (isInView && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isInView, isFetching, fetchNextPage, hasNextPage]);

  return (
    <React.Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%" />
      )}
      {status === 'success' && universities.length === 0 ? (
        <MessageFeedbackView
          message="No universities"
          height="100%"
        />
      ) : (
        <List disablePadding>
          {universities.map(university => (
            <Suspense key={university.id} fallback={<>{renderSkeleton()}</>}>
              {renderItem(university)}
            </Suspense>
          ))}
        </List>
      )}

      {hasNextPage && <Box ref={loadNextRef} />}
      {hasNextPage && _renderSkeleton()}
    </React.Fragment>
  );
};
